/**
 * A hand-built test chamber.
 *
 * This exists for the reason docs/02-movement.md §3 gives: every traversal
 * ability must be fun in an empty room, and you cannot evaluate that claim
 * inside procedurally generated terrain where you can't tell whether a bad jump
 * was the controller or the level. This room is the movement lab — flat ground
 * of several materials, ledges at exact jump-arc heights, a wall-jump shaft,
 * and reservoirs of the materials whose interactions we most need to see.
 *
 * The procedural generator (src/world/) replaces this for actual play.
 */

import { hash2f } from '../core/rng';
import { fbm2 } from '../core/math';
import { MAT } from './materialData';
import { Flag } from './materials';
import { CHUNK_MASK, CHUNK_SHIFT, CHUNK_SIZE, type Chunk } from './world';

/** Ground level in world cells. Jump height is 54, so ledges key off this. */
export const GROUND_Y = 96;

export function testRoomGenerator(chunk: Chunk, cx: number, cy: number): void {
  const baseX = cx << CHUNK_SHIFT;
  const baseY = cy << CHUNK_SHIFT;

  chunk.mat.fill(0);
  chunk.temp.fill(20);
  chunk.flags.fill(0);

  for (let ly = 0; ly < CHUNK_SIZE; ly++) {
    const wy = baseY + ly;
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const wx = baseX + lx;
      const i = (ly << CHUNK_SHIFT) | lx;
      const m = materialAt(wx, wy);
      if (m === 0) continue;
      chunk.mat[i] = m;
      chunk.temp[i] = spawnTempFor(m);
      // Structural cells don't settle — without this the whole room slumps
      // into a pile on the first tick.
      if (isStructural(wx, wy)) chunk.flags[i] |= Flag.STATIC;
      // Stable per-cell visual variance.
      chunk.flags[i] |= (Math.floor(hash2f(wx, wy, 77) * 8) & 7) << Flag.VARIANT_SHIFT;
      chunk.aux[i] = 0;
    }
  }
}

function isStructural(wx: number, wy: number): boolean {
  // Everything except the deliberately loose/liquid deposits.
  return depositAt(wx, wy) === 0;
}

function materialAt(wx: number, wy: number): number {
  const deposit = depositAt(wx, wy);
  if (deposit !== 0) return deposit;

  // --- bedrock floor, with a rough underside so it doesn't read as a slab ---
  if (wy > GROUND_Y + 6) {
    const n = fbm2(wx * 0.02, wy * 0.02, 3, 2, 0.5, 41);
    if (wy > GROUND_Y + 10 + n * 8) return MAT.GRANITE;
    return MAT.LIMESTONE;
  }

  // --- the floor itself, in bands so friction differences are learnable ---
  if (wy > GROUND_Y) {
    if (wx > -40 && wx < 30) return MAT.CONCRETE;
    if (wx >= 30 && wx < 90) return MAT.ICE; // slick band
    if (wx >= 90 && wx < 150) return MAT.FUNGAL_MAT; // grippy band
    if (wx >= 150 && wx < 210) return MAT.SAND; // displaces underfoot
    return MAT.LIMESTONE;
  }

  // --- left wall of the arena ---
  if (wx < -46 && wx > -54 && wy > GROUND_Y - 90) return MAT.GRANITE;

  // --- wall-jump shaft: two facing walls, 26 cells apart ---
  if (wy < GROUND_Y && wy > GROUND_Y - 130) {
    if (wx >= 240 && wx < 246) return MAT.CONCRETE;
    if (wx >= 272 && wx < 278) return MAT.ICE; // fast slide side
  }

  // --- ledges at fractions of max jump height (54) ---
  const ledge = (x0: number, x1: number, y: number, mat: number) =>
    wx >= x0 && wx < x1 && wy >= y && wy < y + 4 ? mat : 0;

  let m = 0;
  m ||= ledge(56, 92, GROUND_Y - 26, MAT.CONCRETE); // half jump
  m ||= ledge(112, 148, GROUND_Y - 50, MAT.CONCRETE); // near max jump
  m ||= ledge(168, 204, GROUND_Y - 74, MAT.WOOD); // needs a wall or dash
  m ||= ledge(-30, 6, GROUND_Y - 38, MAT.WOOD); // flammable ledge
  if (m) return m;

  // --- a ceiling corner to prove corner-correction works ---
  if (wx >= 20 && wx < 48 && wy >= GROUND_Y - 62 && wy < GROUND_Y - 58) {
    return MAT.STEEL;
  }

  return 0;
}

/**
 * Loose and liquid deposits. These are the interaction demos: an oil pool over
 * water (so the float + surface-fire behaviour is visible), an acid pool above
 * limestone, a coal seam, and a snow bank that melts into conductive water.
 */
function depositAt(wx: number, wy: number): number {
  // water basin, sunk into the floor
  if (wx > 300 && wx < 380 && wy > GROUND_Y - 2 && wy < GROUND_Y + 18) {
    if (wy < GROUND_Y + 4) return MAT.OIL; // floats on the water below
    return MAT.WATER;
  }
  // acid pool eating soft stone
  if (wx > 400 && wx < 450 && wy > GROUND_Y - 1 && wy < GROUND_Y + 10) {
    return MAT.ACID;
  }
  // sand pile to burrow through and to displace underfoot
  if (wx > 150 && wx < 210 && wy > GROUND_Y - 14 && wy <= GROUND_Y) {
    const n = fbm2(wx * 0.06, 0, 2, 2, 0.5, 9);
    if (wy > GROUND_Y - 4 - n * 10) return MAT.SAND;
  }
  // coal seam — volatile, for the dust-explosion demo
  if (wx > 470 && wx < 520 && wy > GROUND_Y + 2 && wy < GROUND_Y + 12) {
    return MAT.COAL_DUST;
  }
  // snow bank
  if (wx > -120 && wx < -60 && wy > GROUND_Y - 12 && wy <= GROUND_Y) {
    return MAT.SNOW;
  }
  // a lava pocket, deep, so water+lava→obsidian is reachable
  if (wx > 330 && wx < 372 && wy > GROUND_Y + 42 && wy < GROUND_Y + 58) {
    return MAT.LAVA;
  }
  return 0;
}

function spawnTempFor(m: number): number {
  if (m === MAT.LAVA) return 1150;
  if (m === MAT.ICE || m === MAT.SNOW) return -10;
  return 20;
}

export function testRoomSpawn(): { x: number; y: number } {
  // x=20 keeps clear of the flammable ledge that spans -30..6; spawning inside
  // it reads as "grounded" while actually being stuck in the geometry.
  return { x: 20, y: GROUND_Y - 20 };
}

/** Chunk coords that must exist for the room to be complete. */
export function testRoomBounds(): { cx0: number; cy0: number; cx1: number; cy1: number } {
  return {
    cx0: -2,
    cy0: -1,
    cx1: 5,
    cy1: 2,
  };
}

void CHUNK_MASK;
